import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import type { User } from '../types';

interface UserProfileProps {
  user: User;
  showLogout?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const UserProfile: React.FC<UserProfileProps> = ({ 
  user, 
  showLogout = true, 
  size = 'md',
  className = '' 
}) => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Administrator';
      case 'user':
        return 'User';
      default:
        return role;
    }
  };

  const sizeClasses = {
    sm: {
      avatar: 'h-8 w-8',
      text: 'text-sm',
      subtext: 'text-xs',
      tertiary: 'text-xs',
      button: 'text-xs px-2 py-1'
    },
    md: {
      avatar: 'h-10 w-10',
      text: 'text-base',
      subtext: 'text-sm',
      tertiary: 'text-sm',
      button: 'text-sm px-3 py-1'
    },
    lg: {
      avatar: 'h-12 w-12',
      text: 'text-lg',
      subtext: 'text-base',
      tertiary: 'text-base',
      button: 'text-sm px-4 py-2'
    }
  };

  const currentSize = sizeClasses[size];

  return (
    <div className={`flex items-center space-x-3 ${className}`}>
      <Avatar className={currentSize.avatar}>
        {user.avatar ? (
          <AvatarImage src={user.avatar} alt={user.name} />
        ) : (
          <AvatarFallback className="bg-navy-700 text-gold-400 font-medium">
            {getInitials(user.name)}
          </AvatarFallback>
        )}
      </Avatar>
      <div className="text-right">
        <div className={`text-navy-100 font-medium ${currentSize.text}`}>
          {user.name}
        </div>
        <div className={`text-navy-300 capitalize ${currentSize.subtext}`}>
          {getRoleDisplayName(user.role)}
        </div>
        {user.companyCode && (
          <div className={`text-navy-300 ${currentSize.tertiary}`}>
            Company Code: {user.companyCode}
          </div>
        )}
      </div>
      {showLogout && (
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleLogout}
          className={`text-navy-200 hover:text-white ${currentSize.button}`}
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          Logout
        </Button>
      )}
    </div>
  );
};

export default UserProfile;